<script setup>
/**
 * Fork overlay (new file, no OSS equivalent) — EVA-M6.
 * Account-level custom HTTP tool manager: lists tools, adds one (name/slug/method/endpoint), toggles
 * enabled, or deletes, via EvaCustomToolsAPI. The endpoint is SSRF-validated server-side; an invalid
 * one surfaces an inline error. Tailwind-only; Composition API.
 */
import { reactive, ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import EvaCustomToolsAPI from 'dashboard/api/eva/customTools';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const { t } = useI18n();

const HTTP_METHODS = ['get', 'post'];

const tools = ref([]);
const authOptions = computed(() => [
  { value: 'none', label: t('EVA_CUSTOM_TOOLS.AUTH_NONE') },
  { value: 'bearer', label: t('EVA_CUSTOM_TOOLS.AUTH_BEARER') },
  { value: 'basic', label: t('EVA_CUSTOM_TOOLS.AUTH_BASIC') },
  { value: 'api_key', label: t('EVA_CUSTOM_TOOLS.AUTH_API_KEY') },
]);
const isLoading = ref(false);
const isSaving = ref(false);
const hasError = ref(false);
const draft = reactive({
  name: '',
  slug: '',
  http_method: 'get',
  endpoint: '',
  description: '',
});

const canAdd = computed(
  () =>
    draft.name.trim() !== '' &&
    draft.slug.trim() !== '' &&
    draft.endpoint.trim() !== '' &&
    !isSaving.value
);

const load = async () => {
  isLoading.value = true;
  hasError.value = false;
  try {
    const { data } = await EvaCustomToolsAPI.get();
    tools.value = data;
  } catch {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

const add = async () => {
  if (!canAdd.value) return;
  isSaving.value = true;
  hasError.value = false;
  try {
    await EvaCustomToolsAPI.create({
      name: draft.name.trim(),
      slug: draft.slug.trim(),
      http_method: draft.http_method,
      endpoint: draft.endpoint.trim(),
      description: draft.description,
    });
    Object.assign(draft, { name: '', slug: '', endpoint: '', description: '' });
    await load();
  } catch {
    // most often the server rejected an unsafe endpoint
    hasError.value = true;
  } finally {
    isSaving.value = false;
  }
};

const toggle = async tool => {
  try {
    await EvaCustomToolsAPI.update(tool.id, { enabled: !tool.enabled });
  } catch {
    hasError.value = true;
  } finally {
    await load(); // resync the list (and the one-way Switch) to server state
  }
};

const remove = async id => {
  try {
    await EvaCustomToolsAPI.delete(id);
  } catch {
    hasError.value = true;
  } finally {
    await load();
  }
};

// --- inline edit (M7 7.2, B2) ---
// The slug is intentionally NOT editable: scenarios reference a tool by slug (tool://slug), so a rename
// would silently break those references. Renaming is delete-and-recreate for now (PRD §9 q5).
const editingId = ref(null);
const editDraft = reactive({
  name: '',
  http_method: 'get',
  endpoint: '',
  description: '',
  // advanced (M7 7.5, B2). auth_config is write-only server-side, so the auth fields start empty on
  // edit — re-enter to change credentials, leave blank to keep the saved ones.
  auth_type: 'none',
  authToken: '',
  authUsername: '',
  authPassword: '',
  authHeader: '',
  authValue: '',
  request_body_template: '',
  response_template: '',
  parameters: '[]',
});

const startEdit = tool => {
  editingId.value = tool.id;
  Object.assign(editDraft, {
    name: tool.name,
    http_method: tool.http_method,
    endpoint: tool.endpoint,
    description: tool.description ?? '',
    auth_type: tool.auth_type ?? 'none',
    authToken: '',
    authUsername: '',
    authPassword: '',
    authHeader: '',
    authValue: '',
    request_body_template: tool.request_body_template ?? '',
    response_template: tool.response_template ?? '',
    parameters: JSON.stringify(tool.parameters ?? [], null, 2),
  });
};

const cancelEdit = () => {
  editingId.value = null;
};

// Assemble auth_config from whichever auth fields the user filled; null means "leave the saved one".
const buildAuthConfig = () => {
  if (editDraft.auth_type === 'bearer' && editDraft.authToken) {
    return { token: editDraft.authToken };
  }
  if (
    editDraft.auth_type === 'basic' &&
    (editDraft.authUsername || editDraft.authPassword)
  ) {
    return {
      username: editDraft.authUsername,
      password: editDraft.authPassword,
    };
  }
  if (editDraft.auth_type === 'api_key' && editDraft.authHeader) {
    return { header: editDraft.authHeader, value: editDraft.authValue };
  }
  return null;
};

const saveEdit = async id => {
  if (!editDraft.name.trim() || !editDraft.endpoint.trim()) return;
  let parameters;
  try {
    parameters = JSON.parse(editDraft.parameters || '[]');
  } catch {
    hasError.value = true; // invalid parameters JSON — surface it instead of saving garbage
    return;
  }
  isSaving.value = true;
  hasError.value = false;
  const payload = {
    name: editDraft.name.trim(),
    http_method: editDraft.http_method,
    endpoint: editDraft.endpoint.trim(),
    description: editDraft.description,
    auth_type: editDraft.auth_type,
    request_body_template: editDraft.request_body_template,
    response_template: editDraft.response_template,
    parameters,
  };
  const authConfig = buildAuthConfig();
  if (authConfig) payload.auth_config = authConfig;
  try {
    await EvaCustomToolsAPI.update(id, payload);
    editingId.value = null;
    await load();
  } catch {
    hasError.value = true;
  } finally {
    isSaving.value = false;
  }
};

// --- test a tool (M7 7.5, B2) ---
const testingId = ref(null);
const testResult = ref(null); // { ok, result } | { ok: false, error }

const runTest = async id => {
  testingId.value = id;
  testResult.value = null;
  try {
    const { data } = await EvaCustomToolsAPI.test(id);
    testResult.value = { id, ...data };
  } catch {
    testResult.value = {
      id,
      ok: false,
      error: t('EVA_CUSTOM_TOOLS.TEST_FAILED'),
    };
  } finally {
    testingId.value = null;
  }
};

onMounted(load);

defineExpose({
  load,
  add,
  toggle,
  remove,
  startEdit,
  cancelEdit,
  saveEdit,
  runTest,
  draft,
  editDraft,
});
</script>

<template>
  <div class="flex flex-col gap-4 min-w-0">
    <h3 class="text-sm font-medium text-n-slate-12">
      {{ t('EVA_CUSTOM_TOOLS.TITLE') }}
    </h3>

    <div
      class="flex flex-col gap-4 p-5 rounded-xl bg-n-solid-2 outline outline-1 -outline-offset-1 outline-n-container"
    >
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input
          v-model="draft.name"
          :label="t('EVA_CUSTOM_TOOLS.NAME')"
          :placeholder="t('EVA_CUSTOM_TOOLS.NAME_PLACEHOLDER')"
        />
        <Input
          v-model="draft.slug"
          :label="t('EVA_CUSTOM_TOOLS.SLUG')"
          :placeholder="t('EVA_CUSTOM_TOOLS.SLUG_PLACEHOLDER')"
        />
      </div>
      <div
        class="inline-flex self-start gap-0.5 p-0.5 rounded-lg bg-n-alpha-black2"
      >
        <button
          v-for="method in HTTP_METHODS"
          :key="method"
          type="button"
          class="px-3 py-1 font-mono text-xs uppercase transition-colors rounded-md"
          :class="
            draft.http_method === method
              ? 'bg-n-solid-3 text-n-slate-12'
              : 'text-n-slate-11 hover:text-n-slate-12'
          "
          @click="draft.http_method = method"
        >
          {{ method }}
        </button>
      </div>
      <Input
        v-model="draft.endpoint"
        :label="t('EVA_CUSTOM_TOOLS.ENDPOINT')"
        :placeholder="t('EVA_CUSTOM_TOOLS.ENDPOINT_PLACEHOLDER')"
        :message="t('EVA_CUSTOM_TOOLS.ENDPOINT_HINT')"
      />
      <Input
        v-model="draft.description"
        :label="t('EVA_CUSTOM_TOOLS.DESCRIPTION')"
        :placeholder="t('EVA_CUSTOM_TOOLS.DESCRIPTION_PLACEHOLDER')"
      />
      <div class="flex justify-end">
        <Button
          :label="t('EVA_CUSTOM_TOOLS.ADD')"
          sm
          :is-loading="isSaving"
          :disabled="!canAdd"
          @click="add"
        />
      </div>
    </div>

    <p v-if="hasError" class="text-sm text-n-ruby-11">
      {{ t('EVA_CUSTOM_TOOLS.ERROR') }}
    </p>
    <p v-else-if="isLoading" class="text-sm text-n-slate-11">
      {{ t('EVA_CUSTOM_TOOLS.LOADING') }}
    </p>
    <p
      v-else-if="!tools.length"
      class="px-6 py-6 text-sm text-center border border-dashed rounded-xl border-n-weak text-n-slate-11"
    >
      {{ t('EVA_CUSTOM_TOOLS.EMPTY') }}
    </p>

    <ul
      v-else
      class="flex flex-col rounded-xl bg-n-solid-2 outline outline-1 -outline-offset-1 outline-n-container divide-y divide-n-weak"
    >
      <li
        v-for="tool in tools"
        :key="tool.id"
        class="flex flex-col gap-3 p-4 min-w-0"
      >
        <!-- edit form (slug is read-only — see startEdit note) -->
        <template v-if="editingId === tool.id">
          <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              v-model="editDraft.name"
              :label="t('EVA_CUSTOM_TOOLS.NAME')"
            />
            <Input
              :model-value="tool.slug"
              :label="t('EVA_CUSTOM_TOOLS.SLUG')"
              disabled
            />
          </div>
          <div
            class="inline-flex self-start gap-0.5 p-0.5 rounded-lg bg-n-alpha-black2"
          >
            <button
              v-for="method in HTTP_METHODS"
              :key="method"
              type="button"
              class="px-3 py-1 font-mono text-xs uppercase transition-colors rounded-md"
              :class="
                editDraft.http_method === method
                  ? 'bg-n-solid-3 text-n-slate-12'
                  : 'text-n-slate-11 hover:text-n-slate-12'
              "
              @click="editDraft.http_method = method"
            >
              {{ method }}
            </button>
          </div>
          <Input
            v-model="editDraft.endpoint"
            :label="t('EVA_CUSTOM_TOOLS.ENDPOINT')"
          />
          <Input
            v-model="editDraft.description"
            :label="t('EVA_CUSTOM_TOOLS.DESCRIPTION')"
          />

          <!-- advanced (M7 7.5, B2): auth, request/response templates, parameters -->
          <label class="flex flex-col gap-1">
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('EVA_CUSTOM_TOOLS.AUTH_TYPE') }}
            </span>
            <Select
              v-model="editDraft.auth_type"
              :options="authOptions"
              class="!w-full [&>select]:w-full"
            />
            <span class="text-xs text-n-slate-11">
              {{ t('EVA_CUSTOM_TOOLS.AUTH_HINT') }}
            </span>
          </label>
          <Input
            v-if="editDraft.auth_type === 'bearer'"
            v-model="editDraft.authToken"
            type="password"
            :label="t('EVA_CUSTOM_TOOLS.AUTH_TOKEN')"
          />
          <div
            v-else-if="editDraft.auth_type === 'basic'"
            class="grid grid-cols-1 gap-4 sm:grid-cols-2"
          >
            <Input
              v-model="editDraft.authUsername"
              :label="t('EVA_CUSTOM_TOOLS.AUTH_USERNAME')"
            />
            <Input
              v-model="editDraft.authPassword"
              type="password"
              :label="t('EVA_CUSTOM_TOOLS.AUTH_PASSWORD')"
            />
          </div>
          <div
            v-else-if="editDraft.auth_type === 'api_key'"
            class="grid grid-cols-1 gap-4 sm:grid-cols-2"
          >
            <Input
              v-model="editDraft.authHeader"
              :label="t('EVA_CUSTOM_TOOLS.AUTH_HEADER')"
            />
            <Input
              v-model="editDraft.authValue"
              type="password"
              :label="t('EVA_CUSTOM_TOOLS.AUTH_VALUE')"
            />
          </div>
          <TextArea
            v-model="editDraft.request_body_template"
            auto-height
            min-height="3rem"
            max-height="10rem"
            custom-text-area-class="font-mono !text-xs"
            :label="t('EVA_CUSTOM_TOOLS.BODY_TEMPLATE')"
          />
          <TextArea
            v-model="editDraft.response_template"
            auto-height
            min-height="3rem"
            max-height="10rem"
            custom-text-area-class="font-mono !text-xs"
            :label="t('EVA_CUSTOM_TOOLS.RESPONSE_TEMPLATE')"
          />
          <TextArea
            v-model="editDraft.parameters"
            auto-height
            min-height="4rem"
            max-height="12rem"
            custom-text-area-class="font-mono !text-xs"
            :label="t('EVA_CUSTOM_TOOLS.PARAMETERS')"
            :message="t('EVA_CUSTOM_TOOLS.PARAMETERS_HINT')"
          />

          <div class="flex justify-end gap-2">
            <Button
              faded
              slate
              sm
              :label="t('EVA_CUSTOM_TOOLS.CANCEL')"
              @click="cancelEdit"
            />
            <Button
              sm
              :label="t('EVA_CUSTOM_TOOLS.SAVE')"
              :is-loading="isSaving"
              :disabled="
                isSaving || !editDraft.name.trim() || !editDraft.endpoint.trim()
              "
              @click="saveEdit(tool.id)"
            />
          </div>
        </template>

        <!-- static row -->
        <template v-else>
          <div class="flex items-center justify-between gap-3">
            <div class="flex flex-col gap-1 min-w-0">
              <span
                class="flex items-center gap-2 text-sm font-medium truncate text-n-slate-12"
              >
                {{ tool.name }}
                <span
                  class="inline-flex px-1.5 py-0.5 font-mono text-xs uppercase rounded-md bg-n-alpha-black2 text-n-slate-11"
                >
                  {{ tool.http_method }}
                </span>
              </span>
              <span class="text-xs truncate text-n-slate-11">{{
                tool.endpoint
              }}</span>
            </div>
            <div class="flex items-center gap-2 shrink-0">
              <Switch :model-value="tool.enabled" @change="toggle(tool)" />
              <span class="w-px h-4 bg-n-weak" />
              <Button
                slate
                xs
                ghost
                :label="t('EVA_CUSTOM_TOOLS.TEST')"
                :is-loading="testingId === tool.id"
                @click="runTest(tool.id)"
              />
              <span class="w-px h-4 bg-n-weak" />
              <Button
                icon="i-lucide-pen"
                slate
                xs
                ghost
                :aria-label="t('EVA_CUSTOM_TOOLS.EDIT')"
                @click="startEdit(tool)"
              />
              <Button
                icon="i-lucide-trash"
                slate
                xs
                ghost
                :aria-label="t('EVA_CUSTOM_TOOLS.DELETE')"
                @click="remove(tool.id)"
              />
            </div>
          </div>
          <pre
            v-if="testResult && testResult.id === tool.id"
            class="p-3 mb-0 overflow-x-auto font-mono text-xs whitespace-pre-wrap break-words rounded-lg bg-n-alpha-black2"
            :class="testResult.ok ? 'text-n-teal-11' : 'text-n-ruby-11'"
            >{{ testResult.ok ? testResult.result : testResult.error }}</pre
          >
        </template>
      </li>
    </ul>
  </div>
</template>
